import type {
  Maybe,
  RoleRefInfo,
  RoleTaxonomyRefInfo,
} from '@revelio/data-access';
import { SelectionCategories, type SelectionList } from '@revelio/iso-utility';

import type {
  RoleSelectionListKey,
  SelectionListKey,
} from '../../../shared/selection-lists/types';

// Type union for both V2 and V3 role items
type RoleListItem = RoleTaxonomyRefInfo | RoleRefInfo;

// Type for transformed items - extends the base types with additional properties
type TransformedRoleItem = {
  id: string;
  label: string;
  shortName?: Maybe<string>;
  longName?: Maybe<string>;
  rawTitles?: Array<string>;
  // Normalized parent relationships - all use job_category for consistency
  job_category?: Maybe<string>;
  role_k50?: Maybe<string>;
  role_k150?: Maybe<string>;
  role_k300?: Maybe<string>;
  role_k500?: Maybe<string>;
  role_k1000?: Maybe<string>;
  role_k1250?: Maybe<string>;
  role_k1500?: Maybe<string>;
  role_k5000?: Maybe<string>;
  role_k15000?: Maybe<string>;
};

// All role-related selection categories
export const ROLE_SELECTION_CATEGORIES = new Set([
  SelectionCategories.JOB_CATEGORY,
  SelectionCategories.ROLE_K50,
  SelectionCategories.ROLE_K150,
  SelectionCategories.ROLE_K300,
  SelectionCategories.ROLE_K500,
  SelectionCategories.ROLE_K1000,
  SelectionCategories.ROLE_K1250,
  SelectionCategories.ROLE_K1500,
  SelectionCategories.ROLE_K5000,
  SelectionCategories.ROLE_K15000,
]);

/**
 * Check if a selection list is role-related
 * Overloads allow narrowing when called with SelectionListKey
 */
export function isRoleSelectionList(
  listId: SelectionListKey
): listId is RoleSelectionListKey;
export function isRoleSelectionList(listId: string): boolean;
export function isRoleSelectionList(listId: string): boolean {
  return ROLE_SELECTION_CATEGORIES.has(listId as SelectionCategories);
}

/**
 * Type guard to check if an item is a V3 role taxonomy item
 */
const isRoleTaxonomyRefInfo = (
  item: RoleListItem | null
): item is RoleTaxonomyRefInfo => {
  // The GraphQL query aliases role_k10 as role_k7 in the response
  return item !== null && 'rawTitles' in item && Array.isArray(item.rawTitles);
};

/**
 * Transform V3 role selection list items to have consistent parent properties
 */
export const transformV3RoleListItem = (
  item: RoleListItem | null
): TransformedRoleItem | null => {
  if (!item) return null;

  const transformed: TransformedRoleItem = {
    id: item.id || '',
    label: item.longName || item.shortName || '',
    shortName: item.shortName,
    longName: item.longName,
  };

  // Handle V3 specific properties
  if (isRoleTaxonomyRefInfo(item)) {
    transformed.rawTitles = item.rawTitles;

    // The GraphQL query aliases role_k10 as role_k7
    // We need to check for role_k7 which contains the parent reference
    if ('role_k7' in item && item.role_k7) {
      transformed.job_category = item.role_k7 as string;
    }
    // Copy other V3 parent relationships
    transformed.role_k50 = item.role_k50;
    transformed.role_k150 = item.role_k150;
    transformed.role_k500 = item.role_k500;
    transformed.role_k1500 = item.role_k1500;
    transformed.role_k5000 = item.role_k5000;
    transformed.role_k15000 = item.role_k15000;
  } else {
    // Handle V2 properties (RoleRefInfo)
    const v2Item = item as RoleRefInfo;
    transformed.job_category = v2Item.roleK7;
    transformed.role_k50 = v2Item.roleK50;
    transformed.role_k150 = v2Item.roleK150;
    transformed.role_k300 = v2Item.roleK300;
    transformed.role_k500 = v2Item.roleK500;
    transformed.role_k1000 = v2Item.roleK1000;
    transformed.role_k1250 = v2Item.roleK1250;

    // Add other V2 specific properties
    if (v2Item.topRawTitles) {
      transformed.rawTitles = [v2Item.topRawTitles];
    }
  }

  return transformed;
};

/**
 * Get the appropriate selection category key based on taxonomy version
 */
export const getRoleSelectionKey = (
  category: SelectionCategories,
  isV3: boolean
): string => {
  if (isV3 && category === SelectionCategories.JOB_CATEGORY) {
    return 'role_k7';
  }
  return category;
};

/**
 * Transform a role selection list for V3 compatibility
 */
export const transformRoleSelectionListForV3 = (
  list: Array<RoleListItem | null>,
  listId: SelectionCategories
): SelectionList => {
  const transformedItems = list
    .map(transformV3RoleListItem)
    .filter((item): item is TransformedRoleItem => item !== null);

  return {
    id: listId,
    label: listId,
    value: transformedItems,
  } as SelectionList;
};
